import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useIsMobile } from "../hooks/useIsMobile";
import { useApi } from "../hooks/useApi";

const DOCKER = axios.create({ baseURL: "/" });
DOCKER.interceptors.request.use((cfg) => {
  try {
    const a = JSON.parse(localStorage.getItem("sentinel_auth"));
    if (a?.access_token) cfg.headers.Authorization = `Bearer ${a.access_token}`;
  } catch {}
  return cfg;
});


// ── Shared styles (used by both layouts) ─────────────────────────────────────
const c = {
  card:  { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "8px", padding: "16px" },
  title: { fontSize: "13px", fontWeight: 700, color: "#60a5fa", marginBottom: "12px",
           display: "flex", alignItems: "center", gap: "8px" },
  sep:   { borderTop: "1px solid #1e2a45", margin: "12px 0" },
  warn:  { background: "#1c1208", border: "1px solid #78350f", borderRadius: "6px",
           padding: "8px 12px", fontSize: "11px", color: "#fcd34d" },
  logs:  { background: "#020617", border: "1px solid #1e2a45", borderRadius: "6px",
           padding: "10px", fontFamily: "monospace", fontSize: "10px", color: "#94a3b8",
           maxHeight: "220px", overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" },
  label: { fontSize: "11px", color: "#64748b", minWidth: "90px" },
  val:   { fontSize: "12px", color: "#e2e8f0", fontWeight: 600 },
  row:   { display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", marginBottom: "8px" },
  stat:  (ok) => ({ width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
                    background: ok ? "#4ade80" : "#f87171" }),
  badge: (ok) => ({
    padding: "3px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 700,
    background: ok ? "#052e16" : "#1a0a0a",
    color:      ok ? "#4ade80" : "#f87171",
    border:     `1px solid ${ok ? "#166534" : "#7f1d1d"}`,
  }),
  btn: (v) => ({
    padding: "6px 14px", borderRadius: "5px", border: "none", cursor: "pointer",
    fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
    background: v === "green"  ? "#065f46" : v === "red"    ? "#7f1d1d"
              : v === "blue"   ? "#1e40af" : v === "yellow" ? "#78350f"
              : v === "purple" ? "#4c1d95" : "#1e2a45",
    color:      v === "green"  ? "#6ee7b7" : v === "red"    ? "#fca5a5"
              : v === "blue"   ? "#93c5fd" : v === "yellow" ? "#fcd34d"
              : v === "purple" ? "#c4b5fd" : "#94a3b8",
  }),
  input: { background: "#0a0f1e", border: "1px solid #1e2a45", borderRadius: "4px",
           padding: "6px 10px", color: "#e2e8f0", fontSize: "12px", flex: 1, outline: "none" },
  flash: (type) => ({
    padding: "8px 12px", borderRadius: "6px", fontSize: "12px", marginTop: "10px",
    background: type === "ok" ? "#052e16" : "#1a0a0a",
    border:     `1px solid ${type === "ok" ? "#166534" : "#7f1d1d"}`,
    color:      type === "ok" ? "#4ade80" : "#f87171",
  }),
};

// ── Mobile-only sub-components ────────────────────────────────────────────────
function StatRow({ label, ok, text, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px",
                  padding: "8px 0", borderBottom: "1px solid #0f172a" }}>
      <div style={c.stat(ok)} />
      <span style={{ fontSize: "12px", color: "#64748b", flex: 1 }}>{label}</span>
      <span style={c.badge(ok)}>{text}</span>
      {sub && <span style={{ fontSize: "10px", color: "#334155" }}>{sub}</span>}
    </div>
  );
}

function InfoRow({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "6px 0", borderBottom: "1px solid #0f172a" }}>
      <span style={{ fontSize: "12px", color: "#64748b" }}>{label}</span>
      <span style={{ fontSize: "13px", fontWeight: 600, color: color || "#e2e8f0" }}>{value}</span>
    </div>
  );
}

// ── noVNC Live View ───────────────────────────────────────────────────────────
function MT5LiveView() {
  const [token, setToken] = useState(null);
  const [show, setShow]   = useState(false);

  useEffect(() => {
    try {
      const auth = JSON.parse(localStorage.getItem("sentinel_auth"));
      if (auth?.access_token) setToken(auth.access_token);
    } catch {}
  }, []);

  // Build KasmVNC URL with auto-connect params
  // KasmVNC reads host/port/path/encrypt/autoconnect from URL query string
  const buildMT5Url = () => {
    const host = window.location.hostname;  // sentinel.skyoptiongroup.com
    const port = window.location.port || (window.location.protocol === "https:" ? "443" : "80");
    const encrypt = window.location.protocol === "https:" ? "1" : "0";
    // path= is the WebSocket subpath relative to the host (nginx maps /mt5/websockify → KasmVNC /)
    const params = new URLSearchParams({
      host,
      port,
      path: "mt5/websockify",
      encrypt,
      autoconnect: "1",
      resize: "scale",
    });
    return `/mt5/?${params.toString()}`;
  };

  // Set sentinel_token cookie then open /mt5 — nginx reads cookie for auth
  const openMT5 = (newTab = false) => {
    if (!token) return;
    // Set cookie valid for 8h, same domain, covering all /mt5 subpaths
    const expires = new Date(Date.now() + 8 * 3600 * 1000).toUTCString();
    document.cookie = `sentinel_token=${token}; expires=${expires}; path=/; SameSite=Lax`;
    const url = buildMT5Url();
    if (newTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      setShow(true);
    }
  };

  // iframe src with auto-connect params
  const iframeSrc = show ? buildMT5Url() : null;

  return (
    <div style={{ ...c.card, marginBottom: "10px" }}>
      <div style={{ ...c.title, justifyContent: "space-between" }}>
        <span>🖥️ MT5 Live Terminal</span>
        <div style={{ display: "flex", gap: "8px" }}>
          {token && (
            <button onClick={() => openMT5(true)}
              style={{ ...c.btn("blue"), padding: "4px 10px", fontSize: "11px", border: "none", cursor: "pointer" }}>
              ↗ Open in New Tab
            </button>
          )}
          <button
            style={{ ...c.btn(show ? "red" : "green"), padding: "4px 10px", fontSize: "11px" }}
            onClick={() => show ? setShow(false) : openMT5(false)}
            disabled={!token}>
            {show ? "⊟ Hide" : "⊞ Show Live View"}
          </button>
        </div>
      </div>
      {!token && (
        <div style={c.warn}>Session token not found — please log in first.</div>
      )}
      {token && show && (
        <div style={{ position: "relative", width: "100%", paddingTop: "62%",
                      borderRadius: "6px", overflow: "hidden",
                      border: "1px solid #1e2a45", marginTop: "8px" }}>
          <iframe
            src={iframeSrc}
            title="MT5 Terminal"
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                     border: "none", background: "#000" }}
            allow="fullscreen"
          />
        </div>
      )}
      {token && !show && (
        <div style={{ fontSize: "11px", color: "#475569", marginTop: "4px" }}>
          Access via <code style={{ color: "#60a5fa" }}>sentinel.skyoptiongroup.com/mt5</code> — authenticated users only. Direct IP access is blocked.
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function MT5Terminal() {
  const { isMobile } = useIsMobile();
  // v5.16: the Market Watch buttons follow the live roster from config, so this
  // picker cannot offer a retired instrument (it used to list SP500m and USDX,
  // neither of which this broker even resolves) or omit a newly added one.
  const { data: _intel } = useApi("/intelligence", 60000);
  const SYMBOLS = Object.keys(_intel?.asset_meta || {}).sort();

  const [status,       setStatus]       = useState(null);
  const [loading,      setLoading]      = useState("");
  const [msg,          setMsg]          = useState(null);
  const [logs,         setLogs]         = useState("");
  const [showLogs,     setShowLogs]     = useState(false);
  const [loginForm,    setLoginForm]    = useState({ login: "", password: "", server: "" });
  const [symInput,     setSymInput]     = useState("");
  const [screenshot,   setScreenshot]   = useState(null);
  const [reconnecting, setReconnecting] = useState(false);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 6000);
  };

  const waitForReconnect = useCallback(async () => {
    setReconnecting(true);
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const r = await DOCKER.get("/docker/mt5/status");
        if (r.data?.mt5_connected) {
          setStatus(r.data);
          setReconnecting(false);
          flash("ok", `✅ MT5 reconnected. AutoTrading: ${r.data?.config_algo_enabled ? "Enabled" : "Disabled"}`);
          return;
        }
      } catch {}
    }
    setReconnecting(false);
    flash("err", "MT5 reconnect timeout — check container");
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await DOCKER.get("/docker/mt5/status");
      setStatus(r.data);
    } catch {
      setStatus({ container_running: false, mt5_connected: false });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 8000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const act = async (label, fn) => {
    setLoading(label);
    try {
      const result = await fn();
      if (result?.data?.error) flash("err", result.data.error);
      else flash("ok", `${label} successful`);
      await fetchStatus();
    } catch (e) {
      flash("err", e.response?.data?.error || e.message);
    } finally {
      setLoading("");
    }
  };

  const doLogin = () => {
    const { login, password, server } = loginForm;
    if (!login || !password || !server) { flash("err", "Fill in all login fields"); return; }
    act("Login", () => DOCKER.post("/docker/mt5/login", { login: parseInt(login), password, server }));
  };

  const doAlgoTrading = (enable) =>
    act(enable ? "Enable AutoTrading" : "Disable AutoTrading", async () => {
      const r = await DOCKER.post("/docker/mt5/algo_trading", { enable });
      const d = r.data;
      if (d?.changed === false) flash("ok", `AutoTrading already ${enable ? "enabled" : "disabled"}`);
      else if (d?.restarting) { flash("ok", "✅ Config updated. MT5 terminal restarting..."); waitForReconnect(); }
      else if (d?.error) flash("err", d.error);
      return r;
    });

  const doEnableSymbol = (sym) => {
    if (!sym) { flash("err", "Enter a symbol name"); return; }
    act(`Enable ${sym}`, () => DOCKER.post("/docker/mt5/enable_symbol", { symbol: sym }));
  };

  const doScreenshot = async () => {
    setLoading("Screenshot");
    try {
      const r = await DOCKER.get("/docker/mt5/screenshot");
      if (r.data?.screenshot) setScreenshot(r.data.screenshot);
      else flash("err", r.data?.error || "Screenshot failed");
    } catch (e) { flash("err", e.message); }
    finally { setLoading(""); }
  };

  const fetchLogs = async () => {
    try {
      const r = await DOCKER.get("/docker/mt5/logs?lines=150");
      setLogs(r.data.logs || "No logs");
      setShowLogs(true);
    } catch (e) { setLogs(`Error: ${e.message}`); setShowLogs(true); }
  };

  const acc         = status?.account || {};
  const connected   = status?.mt5_connected;
  const containerOk = status?.container_running;
  const algoOk      = status?.config_algo_enabled ?? status?.terminal?.trade_expert ?? false;
  const busy        = !!loading;

  // ── Shared blocks (used in both layouts) ───────────────────────────────────

  const msgBlock = msg && (
    <div style={c.flash(msg.type)}>{msg.type === "ok" ? "✅" : "❌"} {msg.text}</div>
  );

  const logsBlock = showLogs && (
    <div style={c.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
        <span style={{ fontSize: "11px", color: "#64748b" }}>MT5 Container Logs (last 150 lines)</span>
        <div style={{ display: "flex", gap: "6px" }}>
          <button style={{ ...c.btn(), padding: "2px 8px", fontSize: "10px" }} onClick={fetchLogs}>↻</button>
          <button style={{ ...c.btn("red"), padding: "2px 8px", fontSize: "10px" }}
            onClick={() => setShowLogs(false)}>✕</button>
        </div>
      </div>
      <div style={c.logs}>{logs}</div>
    </div>
  );

  const screenshotBlock = screenshot && (
    <div style={c.card}>
      <div style={{ ...c.title, justifyContent: "space-between" }}>
        <span>📸 MT5 Terminal Screenshot</span>
        <button style={{ ...c.btn("red"), padding: "2px 8px", fontSize: "10px" }}
          onClick={() => setScreenshot(null)}>✕ Close</button>
      </div>
      <img src={`data:image/png;base64,${screenshot}`} alt="MT5 Terminal"
        style={{ width: "100%", borderRadius: "6px", border: "1px solid #1e2a45",
                 touchAction: "pinch-zoom" }} />
    </div>
  );

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ padding: "10px", display: "flex", flexDirection: "column", gap: "10px" }}>

        <MT5LiveView />

        {/* Status */}
        <div style={c.card}>
          <div style={c.title}>
            🖥️ MT5 Terminal
            {reconnecting && <span style={{ fontSize: "11px", color: "#fcd34d" }}>⏳ Reconnecting...</span>}
            <button style={{ ...c.btn(), padding: "4px 10px", fontSize: "11px", marginLeft: "auto" }}
              onClick={fetchStatus}>↻</button>
          </div>
          <StatRow label="Container"   ok={containerOk} text={containerOk ? "Running" : "Stopped"}
                   sub={status?.container_health} />
          <StatRow label="MT5 Bridge"  ok={connected}   text={connected ? "Connected" : "Disconnected"} />
          <StatRow label="AutoTrading" ok={algoOk}      text={algoOk ? "Enabled ✅" : "Disabled ❌"} />
          {connected && (
            <>
              <div style={c.sep} />
              <InfoRow label="Account"   value={acc.login} />
              <InfoRow label="Server"    value={acc.server} />
              <InfoRow label="Type"      value={acc.trade_mode === 0 ? "DEMO" : "LIVE"}
                       color={acc.trade_mode === 0 ? "#60a5fa" : "#4ade80"} />
              <InfoRow label="Balance"   value={`$${acc.balance?.toLocaleString() ?? "—"}`} color="#60a5fa" />
              <InfoRow label="Equity"    value={`$${acc.equity?.toLocaleString() ?? "—"}`}  color="#4ade80" />
              <InfoRow label="Positions" value={status?.open_positions ?? 0} />
            </>
          )}
          {msgBlock}
        </div>

        {/* AutoTrading */}
        <div style={c.card}>
          <div style={c.title}>⚡ AutoTrading</div>
          <div style={c.warn}>
            AutoTrading must be <strong>enabled</strong> for the brain to place orders.
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
            <button style={{ ...c.btn("green"), flex: 1, padding: "10px" }}
              onClick={() => doAlgoTrading(true)} disabled={busy}>
              {loading === "Enable AutoTrading" ? "⏳..." : "✅ Enable"}
            </button>
            <button style={{ ...c.btn("red"), flex: 1, padding: "10px" }}
              onClick={() => doAlgoTrading(false)} disabled={busy}>
              {loading === "Disable AutoTrading" ? "⏳..." : "🚫 Disable"}
            </button>
          </div>
        </div>

        {/* Login */}
        <div style={c.card}>
          <div style={c.title}>🔑 MT5 Login</div>
          {connected && (
            <div style={{ ...c.warn, marginBottom: "10px" }}>
              ⚠️ Connected as {acc.login}@{acc.server}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {[
              { key: "login",    label: "Account #", type: "number",   mode: "numeric",   ph: "12345678" },
              { key: "password", label: "Password",  type: "password", mode: undefined,   ph: "••••••••" },
              { key: "server",   label: "Server",    type: "text",     mode: undefined,   ph: "MetaQuotes-Demo" },
            ].map(({ key, label, type, mode, ph }) => (
              <div key={key}>
                <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>{label}</div>
                <input
                  style={{ ...c.input, flex: undefined, width: "100%", padding: "10px", fontSize: "14px" }}
                  placeholder={ph} type={type} inputMode={mode}
                  value={loginForm[key]}
                  onChange={e => setLoginForm(f => ({ ...f, [key]: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && key === "server" && doLogin()}
                />
              </div>
            ))}
            <button style={{ ...c.btn("green"), width: "100%", padding: "12px", fontSize: "14px" }}
              onClick={doLogin} disabled={busy}>
              {loading === "Login" ? "⏳ Logging in..." : "🔓 Login to MT5"}
            </button>
          </div>
        </div>

        {/* Controls */}
        <div style={c.card}>
          <div style={c.title}>🔧 Controls</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[
              { label: "🔄 Restart Container", v: "yellow", action: () => act("Restart MT5", () => DOCKER.post("/docker/mt5/restart")), key: "Restart MT5" },
              { label: "🔁 Restart Terminal",  v: "purple", action: () => act("Restart Terminal", () => DOCKER.post("/docker/mt5/restart_terminal")), key: "Restart Terminal" },
              { label: `📋 ${showLogs ? "Refresh" : "Show"} Logs`, v: "", action: fetchLogs, key: "logs" },
              { label: "📸 Screenshot",        v: "purple", action: doScreenshot, key: "Screenshot" },
            ].map(({ label, v, action, key }) => (
              <button key={key} style={{ ...c.btn(v), padding: "10px 8px", fontSize: "12px" }}
                onClick={action} disabled={busy}>
                {loading === key ? "⏳..." : label}
              </button>
            ))}
          </div>
        </div>

        {/* Market Watch */}
        <div style={c.card}>
          <div style={c.title}>📊 Market Watch</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "10px" }}>
            {SYMBOLS.map(sym => (
              <button key={sym} style={{ ...c.btn("blue"), padding: "8px 10px", fontSize: "12px" }}
                onClick={() => doEnableSymbol(sym)} disabled={busy}>
                + {sym}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <input style={{ ...c.input, padding: "10px", fontSize: "14px" }}
              placeholder="Custom symbol..."
              value={symInput}
              onChange={e => setSymInput(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && doEnableSymbol(symInput)} />
            <button style={{ ...c.btn("green"), padding: "10px 14px" }}
              onClick={() => doEnableSymbol(symInput)} disabled={busy}>+ Add</button>
          </div>
        </div>

        {screenshotBlock}
        {logsBlock}
      </div>
    );
  }

  // ── DESKTOP LAYOUT (original) ──────────────────────────────────────────────
  return (
    <div style={{ padding: "10px", maxWidth: "1100px", margin: "0 auto",
                  display: "flex", flexDirection: "column", gap: "10px" }}>

      <MT5LiveView />

      {/* Status Bar */}
      <div style={c.card}>
        <div style={c.title}>
          <span>🖥️ MT5 Terminal Status</span>
          {reconnecting && <span style={{ fontSize: "11px", color: "#fcd34d", marginLeft: "8px" }}>
            ⏳ Reconnecting...
          </span>}
          <button style={{ ...c.btn(), padding: "3px 8px", fontSize: "10px", marginLeft: "auto" }}
            onClick={fetchStatus}>↻ Refresh</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
          <div style={c.row}>
            <div style={c.stat(containerOk)} />
            <span style={c.label}>Container</span>
            <span style={c.badge(containerOk)}>{containerOk ? "Running" : "Stopped"}</span>
            <span style={{ fontSize: "10px", color: "#64748b" }}>{status?.container_health}</span>
          </div>
          <div style={c.row}>
            <div style={c.stat(connected)} />
            <span style={c.label}>MT5 Bridge</span>
            <span style={c.badge(connected)}>{connected ? "Connected" : "Disconnected"}</span>
          </div>
          <div style={c.row}>
            <div style={c.stat(algoOk)} />
            <span style={c.label}>AutoTrading</span>
            <span style={c.badge(algoOk)}>{algoOk ? "Enabled" : "Disabled"}</span>
          </div>
        </div>

        {connected && (
          <>
            <div style={c.sep} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
              <div style={c.row}><span style={c.label}>Account</span><span style={c.val}>{acc.login}</span></div>
              <div style={c.row}><span style={c.label}>Server</span><span style={c.val}>{acc.server}</span></div>
              <div style={c.row}><span style={c.label}>Type</span>
                <span style={{ ...c.badge(acc.trade_mode !== 0), fontSize: "11px" }}>
                  {acc.trade_mode === 0 ? "DEMO" : "LIVE"}
                </span>
              </div>
              <div style={c.row}><span style={c.label}>Balance</span><span style={c.val}>${acc.balance?.toLocaleString()}</span></div>
              <div style={c.row}><span style={c.label}>Equity</span><span style={c.val}>${acc.equity?.toLocaleString()}</span></div>
              <div style={c.row}><span style={c.label}>Open Positions</span><span style={c.val}>{status?.open_positions}</span></div>
            </div>
          </>
        )}
        {msgBlock}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>

        {/* Login */}
        <div style={c.card}>
          <div style={c.title}>🔑 MT5 Login</div>
          {connected && (
            <div style={{ ...c.warn, marginBottom: "10px" }}>
              ⚠️ Already connected as {acc.login}@{acc.server}. Login will switch account.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div style={c.row}>
              <span style={c.label}>Account #</span>
              <input style={c.input} placeholder="12345678" type="number"
                value={loginForm.login}
                onChange={e => setLoginForm(f => ({ ...f, login: e.target.value }))} />
            </div>
            <div style={c.row}>
              <span style={c.label}>Password</span>
              <input style={c.input} placeholder="••••••••" type="password"
                value={loginForm.password}
                onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <div style={c.row}>
              <span style={c.label}>Server</span>
              <input style={c.input} placeholder="MetaQuotes-Demo"
                value={loginForm.server}
                onChange={e => setLoginForm(f => ({ ...f, server: e.target.value }))} />
            </div>
            <button style={c.btn("green")} onClick={doLogin} disabled={busy}>
              {loading === "Login" ? "⏳ Logging in..." : "🔓 Login to MT5"}
            </button>
          </div>
        </div>

        {/* AutoTrading + Controls */}
        <div style={c.card}>
          <div style={c.title}>⚡ AutoTrading Control</div>
          <div style={{ ...c.warn, marginBottom: "12px" }}>
            AutoTrading must be <strong>enabled</strong> for the brain to place orders.
            Retcode 10027 = AutoTrading disabled.
          </div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
            <button style={{ ...c.btn("green"), flex: 1 }}
              onClick={() => doAlgoTrading(true)} disabled={busy}>
              {loading === "Enable AutoTrading" ? "⏳..." : "✅ Enable AutoTrading"}
            </button>
            <button style={{ ...c.btn("red"), flex: 1 }}
              onClick={() => doAlgoTrading(false)} disabled={busy}>
              {loading === "Disable AutoTrading" ? "⏳..." : "🚫 Disable AutoTrading"}
            </button>
          </div>

          <div style={c.sep} />
          <div style={c.title}>🔧 Container Controls</div>
          <div style={c.row}>
            <button style={c.btn("yellow")}
              onClick={() => act("Restart MT5", () => DOCKER.post("/docker/mt5/restart"))} disabled={busy}>
              {loading === "Restart MT5" ? "⏳..." : "🔄 Restart MT5 Container"}
            </button>
            <button style={c.btn("purple")}
              onClick={() => act("Restart Terminal", () => DOCKER.post("/docker/mt5/restart_terminal"))} disabled={busy}>
              {loading === "Restart Terminal" ? "⏳..." : "🔁 Restart MT5 Terminal"}
            </button>
            <button style={c.btn()} onClick={fetchLogs} disabled={busy}>
              📋 {showLogs ? "Refresh" : "Show"} Logs
            </button>
            <button style={c.btn("purple")} onClick={doScreenshot} disabled={busy}>
              {loading === "Screenshot" ? "⏳..." : "📸 Screenshot"}
            </button>
          </div>
        </div>
      </div>

      {/* Market Watch */}
      <div style={c.card}>
        <div style={c.title}>📊 Market Watch — Enable Symbols</div>
        <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "10px" }}>
          Symbols must be in Market Watch for the brain to get ticks and place orders.
        </div>
        <div style={c.row}>
          {SYMBOLS.map(sym => (
            <button key={sym} style={c.btn("blue")} onClick={() => doEnableSymbol(sym)} disabled={busy}>
              + {sym}
            </button>
          ))}
        </div>
        <div style={{ ...c.row, marginTop: "8px" }}>
          <input style={{ ...c.input, maxWidth: "180px" }} placeholder="Custom symbol..."
            value={symInput}
            onChange={e => setSymInput(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && doEnableSymbol(symInput)} />
          <button style={c.btn("green")} onClick={() => doEnableSymbol(symInput)} disabled={busy}>
            + Add Symbol
          </button>
        </div>
      </div>

      {screenshotBlock}
      {logsBlock}
    </div>
  );
}
